// The private file store, from the app (Carla's decision 0, 2026-10-12). The server keeps the bytes
// private; a file is opened by a link signed for five minutes, asked for at the moment of opening.
import { api, API_BASE } from '../api/client';

/**
 * Where an <img> should load a stored picture from. A profile photo is saved as an app path,
 * /api/files/avatar/<id>, which redirects to a fresh signed link (Carla's decision 2); the API is not
 * always on the page's own origin, so that path is put on the API's. Anything else (an older photo
 * held as a data: URL, a Google picture, an https link) is used as it is.
 */
export function mediaSrc(src) {
  if (typeof src !== 'string' || !src) return src || undefined;
  return src.startsWith('/api/') ? `${API_BASE}${src}` : src;
}

// The same limits the server enforces (lib/fileStore.js), so a person is told before they wait.
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const FILE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,'
  + '.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,'
  + 'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,'
  + 'application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';
export const FILE_ADVICE = 'Images, PDF, Word, Excel, PowerPoint, text or CSV, up to 10 MB.';

/** A sentence when a chosen file is over the limit, else ''. */
export function fileTooBig(file) {
  return file && file.size > MAX_FILE_BYTES ? `“${file.name}” is over 10 MB. Please choose a smaller file.` : '';
}

export function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/** Upload one file against something (kind + its id). Resolves to the stored file. Throws ApiError. */
export async function uploadStoredFile({ kind, entityId, file }) {
  const fd = new FormData();
  fd.append('kind', kind);
  fd.append('entityId', String(entityId));
  fd.append('file', file);
  const res = await api('/api/files', { method: 'POST', body: fd });
  const body = await res.json();
  if (!body?.file?.id) throw new Error('The file was not saved.');
  return body.file;
}

/**
 * Open a file in a new tab. The tab is opened IN the tap (a tab opened after an await is blocked by
 * Safari), then sent to the signed link once it arrives. getLink resolves to { url }; on failure the
 * blank tab is closed and the error is thrown for the caller to show.
 */
export async function openFileLink(getLink) {
  const win = typeof window !== 'undefined' ? window.open('', '_blank') : null;
  try {
    const body = await getLink();
    if (!body?.url) throw new Error('That file could not be opened.');
    if (win) { win.opener = null; win.location.href = body.url; } else window.location.assign(body.url);
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}

/** Open a stored file the signed-in person may see. */
export function openStoredFile(fileId) {
  return openFileLink(async () => (await api(`/api/files/${encodeURIComponent(fileId)}/link`)).json());
}
